import { useEffect, useState, useCallback, useMemo } from "react";
import api from "@/lib/api";
import { date } from "@/lib/format";
import { PageHeader } from "@/components/Bits";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { Badge } from "@/components/hr/HrBits";
import { DecisionButtons } from "@/pages/HR";

const iso = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
const DOW = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
const typeCls = (t, pending) => {
  const base = /annual/i.test(t) ? ["bg-orange-500 text-white", "border-orange-500 text-orange-300"]
    : /sick/i.test(t) ? ["bg-red-600 text-white", "border-red-500 text-red-300"] : ["bg-sky-700 text-white", "border-sky-500 text-sky-300"];
  return pending ? `border border-dashed bg-transparent ${base[1]}` : `border border-transparent ${base[0]}`;
};
const isHalf = (r, d) => (d === r.start_date && r.start_half) || (d === r.end_date && r.end_half);

function Chip({ r, d }) {
  return (
    <div className={`text-[10px] leading-tight px-1.5 py-0.5 rounded truncate ${typeCls(r.type, r.status === "pending")}`} title={`${r.user_name} · ${r.type} · ${r.status}`} data-testid={`cal-chip-${r.id}-${d}`}>
      {isHalf(r, d) && "½ "}{r.user_name.split(" ")[0]}
    </div>
  );
}

function Legend() {
  const item = (cls, l) => <span className="flex items-center gap-1.5"><span className={`w-4 h-3 rounded ${cls}`} />{l}</span>;
  return (
    <div className="flex flex-wrap gap-4 text-xs text-muted-foreground mb-3" data-testid="cal-legend">
      {item("bg-orange-500", "Annual")}{item("bg-red-600", "Sick")}{item("bg-sky-700", "Other")}
      {item("border border-dashed border-slate-300", "Pending (outlined)")}{item("bg-emerald-500/40", "Bank holiday / closure")}<span>½ = half day</span>
    </div>
  );
}

function DayPanel({ day, rows, onClose, reload }) {
  return (
    <Sheet open={!!day} onOpenChange={(o) => !o && onClose()}>
      <SheetContent className="w-full sm:max-w-md overflow-y-auto" data-testid="cal-day-panel">
        <SheetHeader><SheetTitle className="font-heading uppercase">{day && date(day)} · {rows.length} off</SheetTitle></SheetHeader>
        <div className="mt-4 space-y-3">
          {rows.length === 0 && <p className="text-sm text-muted-foreground">Nobody off.</p>}
          {rows.map((r) => (
            <div key={r.id} className="card-industrial p-3" data-testid={`cal-panel-row-${r.id}`}>
              <div className="flex items-center justify-between gap-2"><span className="font-bold">{r.user_name}</span><Badge status={r.status} /></div>
              <div className="text-xs text-muted-foreground mt-1">{r.location} · {r.type}{isHalf(r, day) ? " · half day" : ""}</div>
              <div className="text-xs mt-1">{date(r.start_date)} – {date(r.end_date)} · {r.days}d</div>
              {r.status === "pending" && <div className="flex gap-2 mt-2"><DecisionButtons r={r} reload={reload} /></div>}
            </div>
          ))}
        </div>
      </SheetContent>
    </Sheet>
  );
}

export default function LeaveCalendar() {
  const [month, setMonth] = useState(() => { const n = new Date(); return new Date(n.getFullYear(), n.getMonth(), 1); });
  const [site, setSite] = useState("all");
  const [sites, setSites] = useState([]);
  const [rows, setRows] = useState([]);
  const [day, setDay] = useState(null);
  const [hols, setHols] = useState({});
  const days = useMemo(() => {
    const first = new Date(month); first.setDate(1 - ((month.getDay() + 6) % 7));
    const last = new Date(month.getFullYear(), month.getMonth() + 1, 0); last.setDate(last.getDate() + (6 - ((last.getDay() + 6) % 7)));
    const out = []; for (let d = new Date(first); d <= last; d.setDate(d.getDate() + 1)) out.push(new Date(d));
    return out;
  }, [month]);
  const load = useCallback(() => {
    const params = { start: iso(days[0]), end: iso(days[days.length - 1]) };
    if (site !== "all") params.location = site;
    api.get("/hr/calendar", { params }).then((r) => setRows(r.data));
    api.get("/hr/holidays", { params: { start: params.start, end: params.end } }).then((r) => setHols(Object.fromEntries(r.data.map((h) => [h.date, h.name]))));
  }, [days, site]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { api.get("/locations").then((r) => setSites(r.data.map((l) => l.name))); }, []);
  const offOn = (d) => { const s = iso(d); return d.getDay() % 6 === 0 || hols[s] ? [] : rows.filter((r) => r.start_date <= s && r.end_date >= s); };
  const today = iso(new Date());
  const shift = (n) => setMonth(new Date(month.getFullYear(), month.getMonth() + n, 1));
  const inMonth = (d) => d.getMonth() === month.getMonth();

  return (
    <div className="p-4 sm:p-6 lg:p-8">
      <PageHeader title="Leave Calendar" subtitle="Who is off each day — approved and pending leave">
        <Select value={site} onValueChange={setSite}><SelectTrigger className="w-40" data-testid="cal-site"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="all">All sites</SelectItem>{sites.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent></Select>
      </PageHeader>
      <div className="flex items-center gap-2 mb-3">
        <Button size="icon" variant="outline" onClick={() => shift(-1)} data-testid="cal-prev"><ChevronLeft className="w-4 h-4" /></Button>
        <Button variant="outline" onClick={() => { const n = new Date(); setMonth(new Date(n.getFullYear(), n.getMonth(), 1)); }} data-testid="cal-today">Today</Button>
        <Button size="icon" variant="outline" onClick={() => shift(1)} data-testid="cal-next"><ChevronRight className="w-4 h-4" /></Button>
        <h2 className="font-heading text-xl font-extrabold uppercase ml-2" data-testid="cal-month">{month.toLocaleString("en-IE", { month: "long", year: "numeric" })}</h2>
      </div>
      <Legend />
      <div className="hidden md:grid grid-cols-7 gap-px bg-border rounded-lg overflow-hidden border border-border" data-testid="cal-grid">
        {DOW.map((d) => <div key={d} className="bg-accent/60 text-[11px] font-mono uppercase tracking-widest text-center py-2">{d}</div>)}
        {days.map((d) => {
          const off = offOn(d); const s = iso(d); const wk = d.getDay() % 6 === 0; const hol = hols[s];
          return (
            <button key={s} onClick={() => !wk && !hol && setDay(s)} data-testid={`cal-day-${s}`}
              className={`text-left min-h-[104px] p-1.5 transition-colors ${hol ? "bg-emerald-500/15 cursor-default" : wk ? "bg-slate-900/80 cursor-default" : "bg-card hover:bg-accent/50"} ${inMonth(d) ? "" : "opacity-40"}`}>
              <div className="flex items-center justify-between mb-1">
                <span className={`font-heading font-extrabold text-sm ${s === today ? "bg-primary text-primary-foreground rounded px-1.5" : ""}`}>{d.getDate()}</span>
                {off.length > 0 && <span className="text-[10px] font-mono text-amber-400" data-testid={`cal-count-${s}`}>{off.length} off</span>}
              </div>
              {hol && <div className="text-[10px] font-bold text-emerald-400 leading-tight mb-1" data-testid={`cal-holiday-${s}`}>{hol}</div>}
              <div className="space-y-0.5">{off.slice(0, 4).map((r) => <Chip key={r.id} r={r} d={s} />)}{off.length > 4 && <div className="text-[10px] text-muted-foreground">+{off.length - 4} more</div>}</div>
            </button>
          );
        })}
      </div>
      <div className="md:hidden space-y-2" data-testid="cal-list">
        {days.filter((d) => inMonth(d) && d.getDay() % 6 !== 0).map((d) => {
          const off = offOn(d); const s = iso(d); const hol = hols[s];
          if (hol) return <div key={s} className="card-industrial p-3 bg-emerald-500/10 text-sm" data-testid={`cal-list-holiday-${s}`}><b>{d.toLocaleDateString("en-IE", { weekday: "short", day: "numeric", month: "short" })}</b> <span className="text-emerald-400 ml-2">{hol}</span></div>;
          return (
            <button key={s} onClick={() => setDay(s)} data-testid={`cal-list-day-${s}`} className={`w-full text-left card-industrial p-3 ${s === today ? "border-primary" : ""}`}>
              <div className="flex justify-between"><span className="font-bold text-sm">{d.toLocaleDateString("en-IE", { weekday: "short", day: "numeric", month: "short" })}</span>
                <span className={`text-xs ${off.length ? "text-amber-400" : "text-muted-foreground"}`}>{off.length ? `${off.length} off` : "All in"}</span></div>
              {off.length > 0 && <div className="flex flex-wrap gap-1 mt-2">{off.map((r) => <Chip key={r.id} r={r} d={s} />)}</div>}
            </button>
          );
        })}
      </div>
      <DayPanel day={day} rows={day ? rows.filter((r) => r.start_date <= day && r.end_date >= day) : []} onClose={() => setDay(null)} reload={load} />
    </div>
  );
}
